import { useEffect, useMemo, useRef, useState, type RefObject } from "react";

export function useIntersectionOnce<T extends HTMLElement>(
  opts: IntersectionObserverInit = { rootMargin: "600px" }
): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [hit, setHit] = useState(false);

  const root = opts.root ?? null;
  const rootMargin = opts.rootMargin ?? "600px";
  const threshold = opts.threshold ?? 0;
  const thresholdIsArray = Array.isArray(opts.threshold);
  const thresholdKey = Array.isArray(threshold)
    ? threshold.join(",")
    : String(threshold);
  const normalizedThreshold = useMemo<IntersectionObserverInit["threshold"]>(() => {
    if (!thresholdIsArray) {
      const parsed = Number.parseFloat(thresholdKey);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    if (!thresholdKey) return [];
    return thresholdKey
      .split(",")
      .filter(Boolean)
      .map((value) => Number.parseFloat(value))
      .filter((value) => Number.isFinite(value));
  }, [thresholdIsArray, thresholdKey]);

  useEffect(() => {
    if (hit) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setHit(true);
        io.disconnect();
      }
    }, { root, rootMargin, threshold: normalizedThreshold });

    io.observe(el);
    return () => io.disconnect();
  }, [hit, root, rootMargin, normalizedThreshold]);

  return [ref, hit];
}
